import type { AccessibleModule } from "@shared/types";
import type { FeatureTour, TourStep } from "../tour.types";

export interface ResolveTourStepsOptions {
  registry: readonly FeatureTour[];
  hasModuleAccess: (module: AccessibleModule) => boolean;
  hasPageAccess: (page: string) => boolean;
  /** Answers whether a feature flag is on; steps with a `requiredFeatureFlag` are hidden when this is omitted */
  isFeatureEnabled?: (flagName: string) => boolean;
  /** Include steps/features flagged enabled: false (e.g. for previewing unreleased tours) */
  includeDisabled?: boolean;
  /** Keep only steps whose `sinceVersion` (default 1) is greater than this — for "what's new" tours */
  sinceVersionGreaterThan?: number;
}

const DEFAULT_STEP_VERSION = 1;

/**
 * Flattens the tour registry into the ordered list of steps the current
 * vendor is allowed to see: features sorted by `order`, features/steps
 * dropped when disabled or when the vendor lacks the required RBAC
 * module/page access or the step's feature flag is off, and capability-gated text overrides applied so a
 * step's text matches what the vendor can actually do on the page. With
 * `sinceVersionGreaterThan`, only steps introduced after that registry
 * version survive, so a returning user sees just the new ones.
 */
export function resolveTourSteps({
  registry,
  hasModuleAccess,
  hasPageAccess,
  isFeatureEnabled,
  includeDisabled = false,
  sinceVersionGreaterThan,
}: ResolveTourStepsOptions): TourStep[] {
  const isEnabled = (enabled?: boolean): boolean => includeDisabled || enabled !== false;

  // With no version filter every step qualifies; otherwise only steps introduced after that registry version do
  const wasIntroducedAfterFilterVersion = (step: TourStep): boolean =>
    sinceVersionGreaterThan === undefined || (step.sinceVersion ?? DEFAULT_STEP_VERSION) > sinceVersionGreaterThan;

  // Fail closed: a flag-gated step needs a positive answer from the flag lookup
  const isFlagSatisfied = (step: TourStep): boolean =>
    includeDisabled || step.requiredFeatureFlag === undefined || isFeatureEnabled?.(step.requiredFeatureFlag) === true;

  const hasAccess = (module?: AccessibleModule, page?: string): boolean =>
    (module === undefined || hasModuleAccess(module)) && (page === undefined || hasPageAccess(page));

  const applyTextOverride = (step: TourStep): TourStep => {
    const override = step.textOverrides?.find((candidate) => hasAccess(candidate.requiredModule, candidate.requiredPage));
    if (!override) return step;
    return {
      ...step,
      titleKey: override.titleKey ?? step.titleKey,
      bodyKey: override.bodyKey ?? step.bodyKey,
      emptyState:
        step.emptyState && override.emptyStateBodyKey !== undefined
          ? { ...step.emptyState, bodyKey: override.emptyStateBodyKey }
          : step.emptyState,
    };
  };

  return [...registry]
    .sort((a, b) => a.order - b.order)
    .filter((feature) => isEnabled(feature.enabled) && hasAccess(feature.requiredModule, feature.requiredPage))
    .flatMap((feature) =>
      feature.steps
        .filter(
          (step) =>
            isEnabled(step.enabled) &&
            hasAccess(step.requiredModule, step.requiredPage) &&
            isFlagSatisfied(step) &&
            wasIntroducedAfterFilterVersion(step)
        )
        .map(applyTextOverride)
    );
}
